import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createCheckoutSession } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { LayoutDashboard, Check, Zap, Shield, TrendingUp } from 'lucide-react';

const FEATURES = [
  'Hybrid AI Transaction Analysis',
  'Unlimited Transactions & Rules',
  'Financial Statements (P&L, Balance Sheet, Trial Balance)',
  'PDF Export for All Reports',
  'Multi-Currency Support',
  'Reconciliation Workflows',
  'Knowledge Graph & Audit Trail',
  'AI Assistant & Sales Chat',
  'Client Profile Management',
  'Budget vs Actual Tracking',
];

export default function PricingPage() {
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = async () => {
    if (!user) {
      navigate('/register');
      return;
    }
    setLoading(true);
    try {
      const res = await createCheckoutSession({
        plan_id: selectedPlan,
        origin_url: window.location.origin,
        num_users: 1,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error('Failed to create checkout session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#F7F6F2' }}>
      {/* Header */}
      <header className="flex items-center justify-between px-8 py-5" style={{ borderBottom: '1px solid #E5E3DB' }}>
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#0A3622' }}>
            <LayoutDashboard size={15} className="text-white" />
          </div>
          <span className="font-heading text-[15px] font-semibold" style={{ color: '#1A1A1A' }}>SnuggleMind</span>
        </Link>
        {user && (
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="font-body text-sm rounded-full" style={{ color: '#4B5563' }}>
            Back to Dashboard
          </Button>
        )}
      </header>

      {/* Pricing Content */}
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="font-heading text-5xl font-medium tracking-tight mb-4" style={{ color: '#1A1A1A' }}>
            <span className="italic" style={{ color: '#D98A4A' }}>Simple</span> Pricing
          </h1>
          <p className="font-body text-lg max-w-xl mx-auto" style={{ color: '#4B5563' }}>
            Start with a 7-day free trial. No credit card required. Upgrade when you're ready.
          </p>
        </div>

        {/* Plan Toggle */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <button
            onClick={() => setSelectedPlan('monthly')}
            className="px-6 py-2.5 rounded-full font-body font-medium text-sm transition-all duration-200"
            style={{
              background: selectedPlan === 'monthly' ? '#1A1A1A' : 'transparent',
              color: selectedPlan === 'monthly' ? '#FFFFFF' : '#4B5563',
              border: '1px solid #E5E3DB',
            }}
            data-testid="plan-toggle-monthly"
          >
            Monthly
          </button>
          <button
            onClick={() => setSelectedPlan('annual')}
            className="px-6 py-2.5 rounded-full font-body font-medium text-sm transition-all duration-200 flex items-center gap-2"
            style={{
              background: selectedPlan === 'annual' ? '#1A1A1A' : 'transparent',
              color: selectedPlan === 'annual' ? '#FFFFFF' : '#4B5563',
              border: '1px solid #E5E3DB',
            }}
            data-testid="plan-toggle-annual"
          >
            Annual
            <span className="px-2 py-0.5 text-[10px] rounded-full font-semibold" style={{ background: '#0A3622', color: '#fff' }}>SAVE 40%</span>
          </button>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Monthly */}
          <div
            className="bg-white rounded-2xl p-8 transition-all duration-300 cursor-pointer"
            style={{
              border: selectedPlan === 'monthly' ? '2px solid #0A3622' : '1px solid #E5E3DB',
              boxShadow: selectedPlan === 'monthly' ? '0 8px 30px rgba(198,156,109,0.15)' : 'none',
            }}
            onClick={() => setSelectedPlan('monthly')}
            data-testid="pricing-card-monthly"
          >
            <div className="flex items-center gap-2 mb-4">
              <Zap size={18} style={{ color: '#D98A4A' }} />
              <span className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold" style={{ color: '#9CA3AF' }}>Monthly</span>
            </div>
            <div className="mb-2">
              <span className="font-heading text-5xl font-medium" style={{ color: '#1A1A1A' }}>$49</span>
              <span className="font-body text-sm ml-1" style={{ color: '#9CA3AF' }}>/user/month</span>
            </div>
            <p className="font-body text-sm mb-6" style={{ color: '#4B5563' }}>Billed monthly. Cancel anytime.</p>
            <Button
              onClick={(e) => { e.stopPropagation(); setSelectedPlan('monthly'); handleCheckout(); }}
              disabled={loading && selectedPlan === 'monthly'}
              className="w-full h-12 rounded-full font-body font-medium text-white transition-colors duration-200"
              style={{ background: selectedPlan === 'monthly' ? '#0A3622' : '#D1D5DB' }}
              data-testid="checkout-monthly-btn"
            >
              {loading && selectedPlan === 'monthly' ? 'Processing...' : 'Get Started'}
            </Button>
          </div>

          {/* Annual */}
          <div
            className="bg-white rounded-2xl p-8 transition-all duration-300 cursor-pointer relative overflow-hidden"
            style={{
              border: selectedPlan === 'annual' ? '2px solid #0A3622' : '1px solid #E5E3DB',
              boxShadow: selectedPlan === 'annual' ? '0 8px 30px rgba(198,156,109,0.15)' : 'none',
            }}
            onClick={() => setSelectedPlan('annual')}
            data-testid="pricing-card-annual"
          >
            {selectedPlan === 'annual' && (
              <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-[10px] font-body font-semibold uppercase tracking-wider" style={{ background: '#0A3622', color: '#fff' }}>
                Best Value
              </div>
            )}
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={18} style={{ color: '#D98A4A' }} />
              <span className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold" style={{ color: '#9CA3AF' }}>Annual</span>
            </div>
            <div className="mb-2">
              <span className="font-heading text-5xl font-medium" style={{ color: '#1A1A1A' }}>$29</span>
              <span className="font-body text-sm ml-1" style={{ color: '#9CA3AF' }}>/user/month</span>
            </div>
            <p className="font-body text-sm mb-6" style={{ color: '#4B5563' }}>Billed annually at $348/year. Save 40%.</p>
            <Button
              onClick={(e) => { e.stopPropagation(); setSelectedPlan('annual'); handleCheckout(); }}
              disabled={loading && selectedPlan === 'annual'}
              className="w-full h-12 rounded-full font-body font-medium text-white transition-colors duration-200"
              style={{ background: selectedPlan === 'annual' ? '#0A3622' : '#D1D5DB' }}
              data-testid="checkout-annual-btn"
            >
              {loading && selectedPlan === 'annual' ? 'Processing...' : 'Get Started'}
            </Button>
          </div>
        </div>

        {/* Features */}
        <div className="mt-16 max-w-2xl mx-auto">
          <h3 className="font-heading text-2xl font-medium text-center mb-8" style={{ color: '#1A1A1A' }}>
            Everything included in <span className="italic" style={{ color: '#D98A4A' }}>every</span> plan
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FEATURES.map((feature, i) => (
              <div key={feature} className="flex items-center gap-3 font-body text-sm" data-testid={`feature-${i}`}>
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(45,106,79,0.1)' }}>
                  <Check size={12} style={{ color: '#2D7A5D' }} />
                </div>
                <span style={{ color: '#4B5563' }}>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Trial CTA */}
        <div className="mt-16 text-center rounded-2xl p-10" style={{ background: '#1A1A1A' }}>
          <Shield size={28} className="mx-auto mb-4" style={{ color: '#D98A4A' }} />
          <h3 className="font-heading text-2xl font-medium mb-3" style={{ color: '#F7F6F2' }}>
            Start your <span className="italic" style={{ color: '#D98A4A' }}>7-day free trial</span>
          </h3>
          <p className="font-body text-sm mb-6" style={{ color: '#9CA3AF' }}>
            No credit card required. Full access to all features during your trial period.
          </p>
          {!user && (
            <Button
              onClick={() => navigate('/register')}
              className="h-12 px-8 rounded-full font-body font-medium text-white transition-colors duration-200"
              style={{ background: '#0A3622' }}
              data-testid="start-trial-btn"
            >
              Start Free Trial
            </Button>
          )}
        </div>
      </div>

      <footer className="max-w-5xl mx-auto px-6 pb-10 flex items-center justify-center gap-4 font-body text-xs" style={{ color: '#9CA3AF' }}>
        <span>&copy; {new Date().getFullYear()} Snugglemind Inc.</span>
        <span>&middot;</span>
        <Link to="/terms" className="hover:underline" style={{ color: '#9CA3AF' }}>Terms of Use</Link>
        <span>&middot;</span>
        <Link to="/privacy" className="hover:underline" style={{ color: '#9CA3AF' }}>Privacy Policy</Link>
        <span>&middot;</span>
        <Link to="/subscription-agreement" className="hover:underline" style={{ color: '#9CA3AF' }}>Subscription Agreement</Link>
      </footer>
    </div>
  );
}
